import React, {useState, useEffect} from 'react'
import {Row, Col, Container, Card, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useLocation, useNavigate} from 'react-router-dom'
import FormWalletCard from './components/FormWalletCard'
import PreviewWalletCard from './components/PreviewWalletCard/PreviewWalletCard'
import Stepper from './components/WizardStep/WalletCardStepper'
import {useI18n} from '../../../Utils/_helpers/useI18n'
import './WalletCard.scss'
import {steps} from '../../../Utils/Constant/WalletCard'
import {CardViewType} from '../../../Utils/Constant/WalletCard'
import {useWalletCard} from './hooks/useWalletCard'

export const WalletCard = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const {t} = useI18n()
  const [isMobile, setIsMobile] = useState(false)
  const [isTablet, setIsTablet] = useState(false)

  const isTemplateParamName = useLocation().pathname.includes('card-templates')
  const isEditParamName = useLocation().pathname.includes('edit')
  // Determine mode from navigation state
  const state = location.state as {
    isEdit?: boolean
    isTemplate?: boolean
  }

  const isEdit = (state?.isEdit || isEditParamName) ?? false
  const isTemplate = (state?.isTemplate || isTemplateParamName) ?? false
  const templateId = new URLSearchParams(location.search).get('template') || null

  const renderMobileLayout = () => {
    if (isMobile) {
      return 12
    } else if (isTablet || isSmallScreen) {
      return 6
    } else {
      return 5
    }
  }

  const renderFormLayout = () => {
    if (isMobile) {
      return 12
    } else if (isTablet || isSmallScreen) {
      return 6
    } else {
      return 7
    }
  }

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth
      setIsMobile(width <= 640)
      setIsTablet(width > 640 && width <= 1024)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  const {
    cardData,
    formFields,
    promoSettingIds,
    setPromoSettingIds,
    vehicleTypeOptionIds,
    setVehicleTypeOptionIds,
    promoActionLabels,
    templateOptions,
    walletCardTemplateID,
    isLoading,
    // states
    isSmallScreen,
    activeSteps,
    completedSteps,
    visitedSteps,
    currentView,
    expandedStep,
    // setters
    setFormFields,
    setWalletCardTemplateID,
    setCompletedSteps,
    setCurrentView,
    setExpandedStep,
    // handlers
    handleCardDataChange,
    handleCardTypeChange,
    handlePromoActionList,
    handleStepClick,
    handleStepExpand,
  } = useWalletCard({isEdit, isTemplate, templateId})

  if (isLoading) {
    return <div>Loading...</div>
  }

  return (
    <Container
      fluid
      className={`wallet-card-form-container ${isMobile ? 'mobile-layout' : ''} ${
        isTablet ? 'tablet-layout' : ''
      }`}
    >
      {/* Page header */}
      <Card.Header
        className={`border-0 d-flex ${
          isMobile ? 'flex-column' : 'justify-content-between'
        } align-items-start pb-2`}
      >
        <div className={`d-flex flex-column header-title ${isMobile ? 'mb-3' : ''}`}>
          <div className='d-flex align-items-center gap-3 mb-2'>
            <OverlayTrigger placement='right' overlay={<Tooltip>Go back to wallet cards</Tooltip>}>
              <span
                className='me-2'
                style={{cursor: 'pointer'}}
                onClick={() => navigate(-1)}
                title='Go back'
              >
                <i className='bi bi-arrow-left fs-4 text-primary'></i>
              </span>
            </OverlayTrigger>
            <h1 className='fw-bolder m-0'>
              {isTemplate
                ? t('MOBILE_WALLET_CARD.FORM.TEMPLATE.TITLE')
                : isEdit
                ? t('COMMON.TITLE.EDIT.FORM', {name: t('WALLET_CARD.LIST.TITLE')})
                : t('COMMON.ACTIONS.CREATE', {name: t('WALLET_CARD.LIST.TITLE')})}
            </h1>
          </div>
          <p className='text-muted fs-6 mt-1'>
            {isTemplate ? (
              <>
                {t('MOBILE_WALLET_CARD.FORM.TEMPLATE.DESCRIPTION_FIRST')}
                <br />
                {t('MOBILE_WALLET_CARD.FORM.TEMPLATE.DESCRIPTION_SECOND')}
              </>
            ) : (
              t('WALLET_CARD.ORDERED_BY_MOST_RECENT')
            )}
          </p>
        </div>
        <div className={`d-flex justify-content-center ${isMobile ? 'w-100' : 'col-6'}`}>
          <Stepper
            steps={steps}
            activeSteps={activeSteps}
            completedSteps={completedSteps}
            onStepClick={handleStepClick}
            isTemplate={isTemplate}
          />
        </div>
      </Card.Header>
      <Row className={`wallet-card-form-row ${isMobile ? 'mobile-stack' : ''}`}>
        {/* Preview card section */}
        <Col
          md={renderMobileLayout()}
          className={`${isMobile ? 'order-2' : 'pl-md-0'} pt-20`}
        >
          <div className={`preview-card-container ${isMobile ? 'mobile-preview' : ''}`}>
            <PreviewWalletCard
              cardData={cardData}
              onCardTypeChange={handleCardTypeChange}
              onCardDataChange={handleCardDataChange}
              onViewChange={(view) => {
                setCurrentView(view)
              }}
              promoSettingIds={promoSettingIds}
              formFields={formFields}
              promoActionLabels={promoActionLabels}
              expandedStep={expandedStep}
              setExpandedStep={setExpandedStep}
            />
          </div>
        </Col>
        {/* Form section */}
        <Col
          md={renderFormLayout()}
          className={`${isMobile ? 'order-1' : 'pr-md-0'} pt-20 ${isMobile ? 'ps-0' : 'ps-60'}`}
        >
          <FormWalletCard
            isTemplate={isTemplate}
            initialData={cardData}
            isEdit={isEdit}
            onDataChange={handleCardDataChange}
            hideJobTitle={currentView === CardViewType.APPLE}
            setCompletedSteps={setCompletedSteps}
            expandedStep={expandedStep}
            onStepExpand={handleStepExpand}
            promoSettingIds={promoSettingIds}
            setPromoSettingIds={setPromoSettingIds}
            vehicleTypeOptionIds={vehicleTypeOptionIds}
            setVehicleTypeOptionIds={setVehicleTypeOptionIds}
            formFields={formFields}
            setFormFields={setFormFields}
            promoActionList={handlePromoActionList}
            setWalletCardTemplateID={setWalletCardTemplateID}
            walletCardTemplateID={walletCardTemplateID}
            templateOptions={templateOptions}
            visitedSteps={visitedSteps}
            viewType={currentView}
          />
        </Col>
      </Row>
    </Container>
  )
}
